"use client";

import { usePathname } from "next/navigation";
import { FLOATING_PAGE_CONTROLS_CLEARANCE_CLASS } from "@/components/floating-page-controls";
import PageBreadcrumb from "@/components/page-breadcrumb";

/**
 * Conditional Layout Wrapper
 * Applies different layout styles based on the current route
 * - Full-width for /specs/* routes
 * - Constrained width for other routes
 */
export default function ConditionalLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const normalizedPath = pathname?.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  const localeRelativePath =
    normalizedPath !== "/" && normalizedPath.endsWith("/")
      ? normalizedPath.slice(0, -1)
      : normalizedPath || "/";

  // Use full-width layout for spec-related pages
  const isFullWidth = pathname?.includes("/specs/");
  const usesConnectorHomeShell = localeRelativePath === "/";
  const usesConnectorOnboardingShell =
    localeRelativePath === "/connectors/new" ||
    localeRelativePath === "/connectors/add-api" ||
    localeRelativePath === "/connectors/add-api/review" ||
    localeRelativePath === "/connectors/authentication";
  const usesConnectorCompletedShell =
    localeRelativePath === "/connectors/completed";
  const usesConnectionDetailShell =
    localeRelativePath === "/specs/actions" ||
    localeRelativePath === "/specs/action" ||
    // /mcp/add is the add form, not an MCP detail page (see mcp/layout.tsx).
    /^\/mcp\/(?!add$)[^/]+$/.test(localeRelativePath);

  // Breadcrumb is optional by convention and should follow design.
  // Keep old PRD behavior by hiding global breadcrumb.
  const shouldShowBreadcrumb = false;

  if (usesConnectorHomeShell) {
    return (
      <main
        className="flex flex-1 bg-white"
        style={{ minHeight: "calc(100vh - var(--app-header-offset, 57px))" }}
      >
        <div className={`flex min-w-0 flex-1 pl-4 pr-5 pt-4 ${FLOATING_PAGE_CONTROLS_CLEARANCE_CLASS}`}>
          <div className="flex min-h-full w-full flex-1 flex-col">
            {shouldShowBreadcrumb && <PageBreadcrumb />}
            {children}
          </div>
        </div>
      </main>
    );
  }

  if (usesConnectorOnboardingShell) {
    return (
      <main
        className="flex flex-1 bg-white"
        style={{ minHeight: "calc(100vh - var(--app-header-offset, 57px))" }}
      >
        <div className="flex min-w-0 flex-1">
          <div className="flex min-h-full w-full flex-1 flex-col">
            {shouldShowBreadcrumb && <PageBreadcrumb />}
            {children}
          </div>
        </div>
      </main>
    );
  }

  if (usesConnectorCompletedShell) {
    return (
      <main
        className="flex flex-1 bg-white"
        style={{ minHeight: "calc(100vh - var(--app-header-offset, 57px))" }}
      >
        <div className="flex min-w-0 flex-1 justify-center pl-4 pr-5 py-4">
          <div className="flex min-h-full w-full max-w-[956px] flex-1 flex-col">
            {shouldShowBreadcrumb && <PageBreadcrumb />}
            {children}
          </div>
        </div>
      </main>
    );
  }

  if (usesConnectionDetailShell) {
    return (
      <main
        className="flex flex-1 bg-white"
        style={{ minHeight: "calc(100vh - var(--app-header-offset, 57px))" }}
      >
        <div className={`flex min-w-0 flex-1 pl-4 pr-5 pt-4 ${FLOATING_PAGE_CONTROLS_CLEARANCE_CLASS}`}>
          <div className="flex min-h-full w-full flex-1 flex-col">
            {shouldShowBreadcrumb && <PageBreadcrumb />}
            {children}
          </div>
        </div>
      </main>
    );
  }


  if (isFullWidth) {
    return (
      <main className={`mx-auto flex w-full max-w-7xl xl:max-w-[1400px] flex-1 flex-col !px-2 pt-8 ${FLOATING_PAGE_CONTROLS_CLEARANCE_CLASS}`}>
        {shouldShowBreadcrumb && <PageBreadcrumb />}
        {children}
      </main>
    );
  }

  return (
    <main className={`mx-auto flex w-full max-w-[1600px] flex-1 flex-col px-8 pt-8 ${FLOATING_PAGE_CONTROLS_CLEARANCE_CLASS}`}>
      {shouldShowBreadcrumb && <PageBreadcrumb />}
      {children}
    </main>
  );
}
